import {
  Card,
  Divider,
  Grid,
  Table,
  TableBody,
  TableHead,
  TableRow,
  Typography,
  Pagination,
  Snackbar,
  Alert as MuiAlert,
  TableContainer,
  CardContent,
  Box,
  Tooltip,
  IconButton,
  CircularProgress,
  Switch,
  FormControlLabel,
  TextField,
  Button
} from '@mui/material'
import { useEffect, useState, forwardRef } from 'react'
import { Helmet } from 'react-helmet-async'
import { useNavigate } from 'react-router-dom'
import { businessCheck, isEmpty } from '../../utils/utils'
import CloudUploadIcon from '@mui/icons-material/CloudUpload'
import ReportRefreshControls from '../../utils/ui_components/ReportRefreshControls'
import useReportRefresh from '../../hooks/useReportRefresh'
import MainCard, {
  SnackBarHideDuration,
  SnackBarTransition,
  StyledTableCell,
  StyledTableRow,
  ShowNoRecordsFound,
  ErrorScreen,
  LoadingDialog
} from '../../utils/ui_utils'
import moment from 'moment'
import { OutletAndDatePickerUI } from '../../utils/ui_components/outletAndDatePickerUi.js'
import ListTypeShimmer from '../../utils/ui_components/shimmers/listTypeShimmer'
import {
  useDownloadReports,
  useFetchItemwiseReport,
  useFetchOutletSummary
} from '../../modules/reports/reportsHooks'
import { useFetchOutlet } from '../../modules/outlet/outletHooks'
import { useDispatch } from 'react-redux'
import { downloadReportsSlice } from '../../modules/reports/reportsSlice.js'
import { generateXLSXCSV } from '../../utils/tsUtils/utils.js'
import { getUserType } from '../../components/tsComponents/AuthGuard'

const Alert = forwardRef(function Alert(props, ref) {
  return <MuiAlert elevation={6} ref={ref} variant="filled" {...props} />
})

const ItemwiseReport = () => {
  const [page, setPage] = useState(1)
  const [limit, setLimit] = useState(20)
  const [pagecount, setPageCount] = useState(1)
  const [selectedOutlet, setSelectedOutlet] = useState('')
  const [snackbar, setSnackBar] = useState(false)
  const [PopUpMessage, setPopUpMessage] = useState(null)
  const [severity, setSeverity] = useState(null)
  const [fromDate, setFromDate] = useState(moment().startOf('day'))
  const [toDate, setToDate] = useState(moment().startOf('day'))
  const [showError, setShowError] = useState(false)
  const [errorMessage, setErrorMessage] = useState('')
  const [showToDateError, setShowToDateError] = useState(false)
  const [toDateErrorMessage, setToDateErrorMessage] = useState(
    'To Date is earlier than From Date'
  )
  const [reportVisibility, setReportVisibility] = useState(false)
  const [goToPageInput, setGoToPageInput] = useState('')
  const { outletFetch, outletFetchData, outletFetchError, outletFetchLoading } =
    useFetchOutlet()
  const {
    itemwiseReportData,
    itemwiseReportError,
    itemwiseReportFetch,
    itemwiseReportLoading
  } = useFetchItemwiseReport()
  const {
    outletSummaryData,
    outletSummaryError,
    outletSummaryFetch,
    outletSummaryLoading
  } = useFetchOutletSummary()

  // Determine user type and build outlets with Owner-only All Outlets
  const userType = getUserType()
  const isOwner = userType === 'Owner'
  const baseOutlets = Array.isArray(outletFetchData?.data)
    ? outletFetchData.data.filter(
        (o) => String(o?._id) !== '0' && o?.name !== 'All Outlets'
      )
    : []
  const outletsWithAll = isOwner
    ? [{ _id: '0', name: 'All Outlets' }, ...baseOutlets]
    : baseOutlets

  // Helper: '0' means All Outlets only for Owner
  const isAllOutletsSelected = isOwner && selectedOutlet === '0'

  const handleSnackbarClose = () => {
    setSnackBar(false)
  }

  const validateAndGetReport = (page) => {
    let tempFromDate = moment(fromDate)
    let tempToDate = moment(toDate)
    if (tempFromDate.isAfter(tempToDate)) {
      return
    }
    // Block fetching for non-owner when no outlet is selected
    if (!isOwner && isEmpty(selectedOutlet)) {
      return
    }
    // Always fetch reports and summary
    fetchRecord(page)
    fetchRecordSummary()
  }

  const fetchRecord = (page) => {
    let payload = {
      from_date: moment(fromDate).format('YYYY-MM-DD'),
      to_date: moment(toDate).add(1, 'days').format('YYYY-MM-DD'),
      pageNumber: page,
      limit: limit
    }

    // Only include outlet_id if a specific outlet is selected
    if (!isAllOutletsSelected && !isEmpty(selectedOutlet)) {
      payload.outlet_id = selectedOutlet
    }

    itemwiseReportFetch(payload)
  }

  const fetchRecordSummary = () => {
    let payload = {
      from_date: moment(fromDate).format('YYYY-MM-DD'),
      to_date: moment(toDate).add(1, 'days').format('YYYY-MM-DD')
    }

    // Only include outlet_id if a specific outlet is selected
    if (!isAllOutletsSelected && !isEmpty(selectedOutlet)) {
      payload.outlet_id = selectedOutlet
    }

    outletSummaryFetch(payload)
  }

  const toastMessage = (message, severity) => {
    setPopUpMessage(message)
    setSeverity(severity)
    setSnackBar(true)
  }

  const handlePageChange = async (event, value) => {
    setPage(value)
    fetchRecord(value)
  }

  const pageCount = (data1) => {
    let count = Math.ceil(data1 / limit)
    setPageCount(count)
  }
  const { downloadReportsLoading, downloadReportsFetch } = useDownloadReports()
  const dispatch = useDispatch()
  const history = useNavigate()

  useEffect(() => {
    dispatch(downloadReportsSlice.actions.reset())
    businessCheck(history)
    let payload = {
      pageNumber: 1,
      limit: 100
    }
    outletFetch(payload)
  }, [])

  useEffect(() => {
    // Default selection based on role after outlets load
    if (isOwner) {
      setSelectedOutlet('0')
    } else if (
      Array.isArray(outletFetchData?.data) &&
      outletFetchData.data.length > 0
    ) {
      setSelectedOutlet(String(outletFetchData.data[0]._id))
    } else {
      setSelectedOutlet('')
    }
  }, [outletFetchData, isOwner])

  useEffect(() => {
    if (itemwiseReportData.data?.length > 0) {
      pageCount(itemwiseReportData.total)
      setReportVisibility(true)
    } else {
      setReportVisibility(false)
    }
  }, [itemwiseReportData])

  const handleOutletChange = (event) => {
    setSelectedOutlet(event.target.value)
  }

  const handleGoToPageChange = (event) => {
    const value = event.target.value
    if (value === '' || /^\d+$/.test(value)) {
      setGoToPageInput(value)
    }
  }

  const handleGoToPageSubmit = () => {
    const pageNumber = parseInt(goToPageInput, 10)
    if (pageNumber >= 1 && pageNumber <= pagecount) {
      setPage(pageNumber)
      validateAndGetReport(pageNumber)
      setGoToPageInput('')
    }
  }

  const handleGoToPageKeyPress = (event) => {
    if (event.key === 'Enter') {
      handleGoToPageSubmit()
    }
  }

  const refreshFunction = () => validateAndGetReport(page)
  const { autoRefresh, handleToggleAutoRefresh, handleManualRefresh } =
    useReportRefresh(refreshFunction, [page, selectedOutlet, fromDate, toDate])

  const fetchOutletOnError = () => {
    let payload = {
      pageNumber: 1,
      limit: 100
    }
    outletFetch(payload)
  }

  const fetchItemwiseReportOnError = () => {
    let payload = {
      from_date: moment(fromDate).format('YYYY-MM-DD'),
      to_date: moment(toDate).add(1, 'days').format('YYYY-MM-DD'),
      pageNumber: page,
      limit: limit
    }

    // Only include outlet_id if an outlet is selected
    if (!isEmpty(selectedOutlet)) {
      payload.outlet_id = selectedOutlet
    }

    itemwiseReportFetch(payload)
  }

  useEffect(() => {
    setPage(1)
    validateAndGetReport(1)
  }, [selectedOutlet, fromDate, toDate])

  const exportToCsv = async () => {
    try {
      let payload = {
        from_date: moment(fromDate).format('YYYY-MM-DD'),
        to_date: moment(toDate).add(1, 'days').format('YYYY-MM-DD'),
        pageNumber: 1,
        limit: 100,
        total: itemwiseReportData?.total || 15,
        type: 'itemwise'
      }

      // Only include outlet_id if a specific outlet is selected
      if (!isAllOutletsSelected && !isEmpty(selectedOutlet)) {
        payload.outlet_id = selectedOutlet
      }

      const reports = await downloadReportsFetch(payload)

      let data1 = [...reports.payload]

      // Add summary row only if outlet summary data exists
      if (outletSummaryData && outletSummaryData.length > 0) {
        data1.push(
          {}, // to add empty row
          {
            IName: 'Total Amount',
            Total_GST: outletSummaryData[0]?.Total_GST?.toFixed(2) || '0.00',
            Total_Cess: outletSummaryData[0]?.Total_Cess?.toFixed(2) || '0.00',
            Total_Disc: outletSummaryData[0]?.Total_Disc?.toFixed(2) || '0.00',
            Total: outletSummaryData[0]?.Total_Sales?.toFixed(2) || '0.00'
          }
        )
      }

      const options = {
        keys: [
          { field: 'IName', title: 'Name' },
          { field: 'IPrice', title: 'Price' },
          { field: 'Total_Qty', title: 'Quantity' },
          { field: 'Total_GST', title: 'GST' },
          { field: 'Total_Cess', title: 'Cess' },
          { field: 'Total_Disc', title: 'Discount' },
          { field: 'Total', title: 'Total' }
        ]
      }

      let outletName = 'All_Outlets'
      if (!isAllOutletsSelected) {
        const selectedOutletData = outletFetchData.data.find(
          (outlet) => String(outlet._id) === String(selectedOutlet)
        )
        outletName = selectedOutletData?.name || 'Unknown_Outlet'
      }

      let filename = `ItemwiseReport_${outletName}`
      filename += `_${payload.from_date}_to_${payload.to_date}`

      const csv = await generateXLSXCSV(data1, options.keys, filename)

      toastMessage('CSV Downloaded Successfully')
    } catch (error) {
      console.error('Error while converting JSON to CSV', error)
      toastMessage('Error While Downloading csv', 'error')
    }
  }

  return (
    <>
      <Helmet title="Itemwise Report" />
      <Grid
        container
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}
      >
        <Grid item>
          <Typography variant="h4" gutterBottom display="inline">
            Itemwise Report
          </Typography>
        </Grid>
        <Grid item>
          <ReportRefreshControls
            autoRefresh={autoRefresh}
            onToggleAutoRefresh={handleToggleAutoRefresh}
            onManualRefresh={handleManualRefresh}
            isLoading={itemwiseReportLoading}
          />
        </Grid>
      </Grid>
      <Divider sx={{ marginTop: 2 }} />
      {outletFetchError ? (
        <ErrorScreen
          onClick={() => {
            fetchOutletOnError()
          }}
        />
      ) : (
        <div>
          {
            <OutletAndDatePickerUI
              outlets={outletsWithAll}
              onOutletChange={handleOutletChange}
              selectedOutlet={selectedOutlet}
              fromDate={fromDate}
              toDate={toDate}
              onFromDateChange={(newValue) => {
                newValue.setHours(0, 0, 0)
                setFromDate(newValue)
                if (moment(toDate).isBefore(newValue)) {
                  setToDateErrorMessage('To Date is earler than From Date')
                  setShowToDateError(true)
                } else {
                  setToDateErrorMessage('')
                  setShowToDateError(false)
                }
              }}
              onFromDateError={(reason, value) => {
                if (reason) {
                  setErrorMessage(reason)
                  setShowError(true)
                } else {
                  setErrorMessage('')
                  setShowError(false)
                }
              }}
              onToDateChange={(newValue) => {
                newValue.setHours(0, 0, 0)
                setToDate(newValue)
                if (moment(fromDate).isAfter(newValue)) {
                  setToDateErrorMessage('To Date is earler than From Dates')
                  setShowToDateError(true)
                } else {
                  setToDateErrorMessage('')
                  setShowToDateError(false)
                }
              }}
              onToDateError={(reason, value) => {
                if (reason) {
                  setToDateErrorMessage(reason)
                  setShowToDateError(true)
                } else {
                  setToDateErrorMessage('')
                  setShowToDateError(false)
                }
              }}
              showFromDateError={showError}
              showToDateError={showToDateError}
              fromDateErrorMessage={errorMessage}
              toDateErrorMessage={toDateErrorMessage}
              exportComponent={
                itemwiseReportData.data &&
                itemwiseReportData.data.length > 0 && (
                  <Tooltip title="Export to CSV" placement="top" arrow>
                    <IconButton
                      variant="contained"
                      onClick={exportToCsv}
                      disabled={downloadReportsLoading}
                      size="large"
                    >
                      {downloadReportsLoading && <CircularProgress size={20} />}
                      {!downloadReportsLoading ? (
                        <>
                          {' '}
                          <CloudUploadIcon fontSize="large" />
                        </>
                      ) : (
                        <> </>
                      )}
                    </IconButton>
                  </Tooltip>
                )
              }
            />
          }
          {itemwiseReportError ? (
            <ErrorScreen
              onClick={() => {
                fetchItemwiseReportOnError()
              }}
            />
          ) : (
            <div>
              {(itemwiseReportLoading == true ||
                itemwiseReportLoading == null ||
                outletFetchLoading) && (
                <Grid
                  container
                  spacing={10}
                  direction={'column'}
                  sx={{ paddingTop: 5 }}
                >
                  <Grid item>
                    <Grid
                      container
                      rowSpacing={4.5}
                      columnSpacing={10}
                      sx={{ mt: 1 }}
                    >
                      <Grid item xs sm md={8}>
                        <MainCard content={false} sx={{ mt: 1.5 }}>
                          <Box sx={{ pt: 1, pr: 2, pl: 2, pb: 1 }}>
                            <ListTypeShimmer />
                          </Box>
                        </MainCard>
                      </Grid>
                    </Grid>
                  </Grid>
                </Grid>
              )}

              {!itemwiseReportLoading &&
                !itemwiseReportError &&
                !outletFetchLoading &&
                !outletFetchError &&
                reportVisibility && (
                  <Grid
                    container
                    spacing={10}
                    direction={'column'}
                    sx={{ paddingTop: 10 }}
                  >
                    <Grid item>
                      <Grid container spacing={10}>
                        <Grid item xs sm md={10}>
                          <MainCard
                            content={false}
                            sx={{
                              width: '100%',
                              maxWidth: 'calc(100vw - 300px)', // Account for sidebar width
                              '@media (max-width: 960px)': {
                                maxWidth: '100vw' // Full width on mobile when sidebar is collapsed
                              }
                            }}
                          >
                            <CardContent sx={{ flexGrow: 1 }}>
                              <TableContainer
                                component={Card}
                                sx={{
                                  width: '100%',
                                  maxWidth: '100%',
                                  overflow: 'auto'
                                }}
                              >
                                <Table size="small">
                                  <TableHead size="small">
                                    <StyledTableRow>
                                      <StyledTableCell sx={{ minWidth: 200 }}>
                                        Name
                                      </StyledTableCell>
                                      <StyledTableCell align="right">
                                        {' '}
                                        Price
                                      </StyledTableCell>
                                      <StyledTableCell align="right">
                                        {' '}
                                        Qty
                                      </StyledTableCell>
                                      <StyledTableCell align="right">
                                        {' '}
                                        GST
                                      </StyledTableCell>
                                      <StyledTableCell align="right">
                                        {' '}
                                        CESS
                                      </StyledTableCell>
                                      <StyledTableCell align="right">
                                        {' '}
                                        Discount
                                      </StyledTableCell>
                                      <StyledTableCell align="right">
                                        {' '}
                                        Total
                                      </StyledTableCell>
                                    </StyledTableRow>
                                  </TableHead>
                                  {outletSummaryData &&
                                    outletSummaryData.length > 0 && (
                                      <TableBody>
                                        <TableRow
                                          sx={{
                                            backgroundColor: '#ECF9FF'
                                          }}
                                        >
                                          <StyledTableCell
                                            align="left"
                                            sx={{ fontWeight: 'bold' }}
                                          >
                                            TOTAL:
                                          </StyledTableCell>
                                          <StyledTableCell
                                            sx={{ fontWeight: 'bold' }}
                                            align="right"
                                          >
                                            -
                                          </StyledTableCell>
                                          <StyledTableCell
                                            sx={{ fontWeight: 'bold' }}
                                            align="right"
                                          >
                                            -
                                          </StyledTableCell>
                                          <StyledTableCell
                                            sx={{ fontWeight: 'bold' }}
                                            align="right"
                                          >
                                            {outletSummaryData[0]?.Total_GST.toFixed(
                                              2
                                            )}
                                          </StyledTableCell>
                                          <StyledTableCell
                                            sx={{ fontWeight: 'bold' }}
                                            align="right"
                                          >
                                            {outletSummaryData[0]?.Total_Cess.toFixed(
                                              2
                                            )}
                                          </StyledTableCell>
                                          <StyledTableCell
                                            sx={{ fontWeight: 'bold' }}
                                            align="right"
                                          >
                                            {outletSummaryData[0]?.Total_Disc.toFixed(
                                              2
                                            )}
                                          </StyledTableCell>
                                          <StyledTableCell
                                            sx={{ fontWeight: 'bold' }}
                                            align="right"
                                          >
                                            {outletSummaryData[0]?.Total_Sales.toFixed(
                                              2
                                            )}
                                          </StyledTableCell>
                                        </TableRow>
                                      </TableBody>
                                    )}
                                  <TableBody>
                                    {itemwiseReportData.data?.map((row, i) => (
                                      <StyledTableRow key={i} hover>
                                        <StyledTableCell sx={{ minWidth: 200 }}>
                                          {row.IName}
                                        </StyledTableCell>
                                        <StyledTableCell align="right">
                                          {row.IPrice.toFixed(2)}
                                        </StyledTableCell>
                                        <StyledTableCell align="right">
                                          {row.Total_Qty.toFixed(3)}
                                        </StyledTableCell>
                                        <StyledTableCell align="right">
                                          {row.Total_GST.toFixed(2)}
                                        </StyledTableCell>
                                        <StyledTableCell align="right">
                                          {row.Total_Cess.toFixed(2)}
                                        </StyledTableCell>
                                        <StyledTableCell align="right">
                                          {row.Total_Disc.toFixed(2)}
                                        </StyledTableCell>
                                        <StyledTableCell align="right">
                                          {row.Total.toFixed(2)}
                                        </StyledTableCell>
                                      </StyledTableRow>
                                    ))}
                                  </TableBody>
                                </Table>
                              </TableContainer>
                            </CardContent>
                            {pagecount > 1 && (
                              <Box
                                sx={{
                                  display: 'flex',
                                  flexDirection: 'column',
                                  gap: 2,
                                  p: 2,
                                  backgroundColor: 'grey.50',
                                  borderTop: '1px solid',
                                  borderColor: 'divider'
                                }}
                              >
                                <Box
                                  sx={{
                                    display: 'flex',
                                    justifyContent: 'center',
                                    width: '100%'
                                  }}
                                >
                                  <Pagination
                                    count={pagecount}
                                    page={page}
                                    color="secondary"
                                    size="small"
                                    boundaryCount={2}
                                    siblingCount={0}
                                    showFirstButton
                                    showLastButton
                                    onChange={handlePageChange}
                                    sx={{
                                      '& .MuiPaginationItem-root': {
                                        transition: 'all 0.2s ease-in-out'
                                      }
                                    }}
                                  />
                                </Box>
                                <Box
                                  sx={{
                                    display: 'flex',
                                    justifyContent: 'center',
                                    alignItems: 'center',
                                    gap: 1.5,
                                    backgroundColor: 'white',
                                    p: 2,
                                    borderRadius: 1,
                                    boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
                                    transition: 'box-shadow 0.2s ease-in-out',
                                    '&:hover': {
                                      boxShadow: '0 2px 6px rgba(0,0,0,0.12)'
                                    }
                                  }}
                                >
                                  <Typography
                                    variant="body2"
                                    sx={{
                                      color: 'text.secondary',
                                      fontWeight: 500
                                    }}
                                  >
                                    Jump to
                                  </Typography>
                                  <TextField
                                    size="small"
                                    value={goToPageInput}
                                    onChange={handleGoToPageChange}
                                    onKeyPress={handleGoToPageKeyPress}
                                    placeholder="Page"
                                    sx={{
                                      width: '80px',
                                      '& .MuiOutlinedInput-root': {
                                        backgroundColor: 'white',
                                        transition: 'all 0.2s ease-in-out',
                                        '& fieldset': {
                                          borderColor: 'grey.300',
                                          borderWidth: '1px'
                                        },
                                        '&:hover fieldset': {
                                          borderColor: 'secondary.main',
                                          borderWidth: '1.5px'
                                        },
                                        '&.Mui-focused fieldset': {
                                          borderColor: 'secondary.main',
                                          borderWidth: '2px',
                                          boxShadow:
                                            '0 0 0 3px rgba(156, 39, 176, 0.1)'
                                        }
                                      },
                                      '& .MuiOutlinedInput-input': {
                                        textAlign: 'center',
                                        fontWeight: 500,
                                        padding: '7px 8px',
                                        height: '20px',
                                        lineHeight: '20px'
                                      }
                                    }}
                                  />
                                  <Typography
                                    variant="body2"
                                    sx={{
                                      color: 'text.secondary',
                                      fontWeight: 400
                                    }}
                                  >
                                    of {pagecount}
                                  </Typography>
                                  <Button
                                    variant="contained"
                                    color="secondary"
                                    size="small"
                                    onClick={handleGoToPageSubmit}
                                    disabled={
                                      !goToPageInput ||
                                      parseInt(goToPageInput, 10) < 1 ||
                                      parseInt(goToPageInput, 10) > pagecount
                                    }
                                    sx={{
                                      textTransform: 'none',
                                      fontWeight: 500,
                                      px: 2,
                                      py: 0.75,
                                      height: '34px',
                                      lineHeight: 1,
                                      transition: 'all 0.2s ease-in-out',
                                      '&:hover': {
                                        transform: 'translateY(-1px)',
                                        boxShadow:
                                          '0 4px 8px rgba(156, 39, 176, 0.3)'
                                      },
                                      '&:active': {
                                        transform: 'translateY(0)'
                                      }
                                    }}
                                  >
                                    Go
                                  </Button>
                                </Box>
                              </Box>
                            )}
                          </MainCard>
                        </Grid>
                      </Grid>
                    </Grid>
                  </Grid>
                )}
              {!itemwiseReportLoading &&
                !itemwiseReportError &&
                !outletFetchLoading &&
                !outletFetchError &&
                !reportVisibility && <ShowNoRecordsFound />}
            </div>
          )}
        </div>
      )}

      <div>
        <Snackbar
          open={snackbar}
          autoHideDuration={SnackBarHideDuration}
          anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
          onClose={handleSnackbarClose}
          TransitionComponent={SnackBarTransition}
        >
          <Alert onClose={handleSnackbarClose} severity={severity}>
            {PopUpMessage}
          </Alert>
        </Snackbar>
      </div>

      <LoadingDialog open={downloadReportsLoading} />
    </>
  )
}

export default ItemwiseReport
